// ─────────────────────────────────────────────────────────────────
// LiveTelemetryCard — HUD direito do LiveRouteMonitor.
// ─────────────────────────────────────────────────────────────────
// Card vertical (estilo telemetria automóvel) no canto inferior
// direito, abaixo do overlay "GPS activo". Mostra Status / Data /
// Velocidade / Distância percorrida / Distância total. Title bar
// muda de cor consoante o estado do veículo:
//   • verde            — em movimento no caminho certo
//   • verde piscante   — a regressar a casa
//   • amarelo          — ao ralenti
//   • vermelho         — parado ou em desvio
// Substitui o antigo "returningChip" do LiveRouteMonitor.
// ─────────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import { usePlannerTheme } from '../../theme/plannerTheme';

const STATE_COLORS = {
  moving:    { bg: '#16a34a', label: 'Em movimento' },
  returning: { bg: '#16a34a', label: 'Em regresso a casa' }, // pulsa
  idle:      { bg: '#eab308', label: 'Ao ralenti' },
  stopped:   { bg: '#ef4444', label: 'Parado' },
  off_route: { bg: '#ef4444', label: 'Em desvio' },
  no_gps:    { bg: '#64748b', label: 'Sem sinal GPS' },
};

function fmtKm(m) {
  if (!Number.isFinite(m) || m <= 0) return '0,000 KM';
  const km = m / 1000;
  return `${km.toFixed(3).replace('.', ',')} KM`;
}

function fmtClock(ts) {
  if (!ts) return '—';
  const d = ts instanceof Date ? ts : new Date(ts);
  if (isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}:${ss}`;
}

// Pequenos ícones SVG inline (24×24) — sem dependência. A cor (stroke) vem
// por prop para seguir o tema (claro/escuro).
const IconClipboard = ({ c }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="3" width="6" height="4" rx="1" /><rect x="5" y="5" width="14" height="16" rx="2" /><path d="M8 11h8M8 15h5" />
  </svg>
);
const IconCalendar = ({ c }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="16" rx="2" /><path d="M16 3v4M8 3v4M3 11h18" />
  </svg>
);
const IconGauge = ({ c }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 14l4-4" /><circle cx="12" cy="14" r="1.5" fill={c} /><path d="M3 17a9 9 0 1 1 18 0" />
  </svg>
);
const IconRoute = ({ c }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="6" cy="19" r="2" /><circle cx="18" cy="5" r="2" /><path d="M8 19h4a4 4 0 0 0 4-4V9a4 4 0 0 1 4-4" />
  </svg>
);
const IconTarget = ({ c }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="4" /><circle cx="12" cy="12" r="1" fill={c} />
  </svg>
);

export default function LiveTelemetryCard({
  // 'moving' | 'idle' | 'stopped' | 'off_route' | 'returning' | 'no_gps'
  state,
  // String custom (ex.: o label do estado actual). Se vier, sobrepõe o default.
  customLabel,
  // Velocidade actual (km/h)
  speedKmh,
  // ISO ou Date do último fix
  timestamp,
  // Metros percorridos no trail real desta rota
  distanceTravelledM,
  // Metros totais planeados (route.totalDistanceM)
  distanceTotalM,
  // Posição absoluta do card no overlay (default direita/baixo do GPS chip)
  style,
}) {
  const cfg = STATE_COLORS[state] || STATE_COLORS.stopped;
  const label = customLabel || cfg.label;
  const isReturning = state === 'returning';

  // Paleta por tema (segue o avatar). Claro = cartão branco limpo; Escuro =
  // visual de telemetria escuro (como antes). A barra de estado fica colorida
  // nos dois.
  const dark = usePlannerTheme() === 'dark';
  const pal = dark
    ? { cardBg: 'rgba(15,23,42,0.86)', border: 'rgba(255,255,255,0.06)', shadow: '0 10px 28px rgba(0,0,0,0.55)', sub: '#94a3b8', value: '#f1f5f9', icon: '#cbd5e1' }
    : { cardBg: 'rgba(255,255,255,0.97)', border: '#e2e8f0', shadow: '0 10px 28px rgba(15,23,42,0.16)', sub: '#64748b', value: '#0f172a', icon: '#94a3b8' };

  // Posição arrastável. Por defeito ancorada no canto superior direito;
  // depois de o operador puxar a title bar, fica nas coordenadas escolhidas.
  // Mesmo padrão do painel "Em trânsito" no canto inferior esquerdo, para
  // o operador poder afastar este HUD sem perder os controlos do mapa.
  const [dragPos, setDragPos] = useState(null);
  const positionStyle = dragPos
    ? { left: dragPos.left, top: dragPos.top, right: 'auto' }
    : { right: 12, top: 56 };

  function onTitleMouseDown(e) {
    if (e.button !== 0) return;
    // Arrancamos o drag a partir da posição actual do panel — se ainda
    // estiver ancorado à direita, calculamos a equivalência em left/top
    // antes de começar para evitar "salto" na primeira frame.
    const panel = e.currentTarget.parentElement;
    const rect = panel.getBoundingClientRect();
    let startLeft = dragPos?.left ?? rect.left;
    let startTop = dragPos?.top ?? rect.top;
    const startX = e.clientX;
    const startY = e.clientY;
    const onMove = (ev) => {
      const nextLeft = Math.max(4, startLeft + (ev.clientX - startX));
      const nextTop = Math.max(4, startTop + (ev.clientY - startY));
      setDragPos({ left: nextLeft, top: nextTop });
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    e.preventDefault();
  }

  return (
    <Box
      style={{
        position: 'absolute', zIndex: 6,
        width: 230,
        background: pal.cardBg,
        color: pal.value,
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: pal.shadow,
        backdropFilter: 'blur(8px)',
        border: `1px solid ${pal.border}`,
        fontFamily: '-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif',
        ...positionStyle,
        ...(style || {}),
      }}
    >
      <style>{`
        @keyframes liveTelemetryBlink {
          0%, 100% { filter: brightness(1); box-shadow: inset 0 0 0 0 rgba(255,255,255,0); }
          50%      { filter: brightness(1.35); box-shadow: inset 0 0 0 2px rgba(255,255,255,0.5); }
        }
      `}</style>

      {/* Title bar — cor pelo estado, pisca quando regresso, arrastável.
          Cursor "move" para sinalizar que o operador pode mover o card. */}
      <Box
        onMouseDown={onTitleMouseDown}
        style={{
          background: cfg.bg,
          padding: '7px 12px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          animation: isReturning ? 'liveTelemetryBlink 1.2s ease-in-out infinite' : 'none',
          cursor: 'move',
          userSelect: 'none',
        }}
      >
        <Typography style={{
          fontSize: '0.7rem', fontWeight: 800, color: '#fff',
          letterSpacing: '0.05em', textTransform: 'uppercase',
        }}>
          {label}
        </Typography>
        {isReturning && (
          <span style={{ fontSize: 14, lineHeight: 1 }}>🏠</span>
        )}
      </Box>

      {/* Lista de linhas — ícone + texto */}
      <Box style={{ padding: '8px 12px 10px', display: 'grid', rowGap: 6 }}>
        <Row icon={<IconClipboard c={pal.icon} />} label="Status" value={label} pal={pal} />
        <Row icon={<IconCalendar c={pal.icon} />} label="Data" value={fmtClock(timestamp)} pal={pal} />
        <Row icon={<IconGauge c={pal.icon} />} label="Velocidade" value={`${Math.max(0, Math.round(Number(speedKmh) || 0))} km/h`} pal={pal} />
        <Row icon={<IconRoute c={pal.icon} />} label="Distância percorrida" value={fmtKm(distanceTravelledM)} pal={pal} />
        <Row icon={<IconTarget c={pal.icon} />} label="Distância total" value={fmtKm(distanceTotalM)} pal={pal} />
      </Box>
    </Box>
  );
}

// Linha do card. Label e valor empilhados, ícone alinhado pela linha
// principal — emula o layout da imagem de referência (cliente).
function Row({ icon, label, value, pal }) {
  return (
    <Box style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
      <Box style={{ marginTop: 1, flexShrink: 0 }}>{icon}</Box>
      <Box style={{ minWidth: 0, flex: 1 }}>
        <Typography style={{
          fontSize: '0.56rem', color: pal.sub, fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: '0.06em', lineHeight: 1.1,
        }}>
          {label}
        </Typography>
        <Typography style={{
          fontSize: '0.74rem', color: pal.value, fontWeight: 700,
          fontVariantNumeric: 'tabular-nums', lineHeight: 1.2,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }} title={String(value)}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}
